/**
 * منصّات التواصل + الربط مع نوع الميديا + بناء روابط المشاركة
 * يُستعمَل في:
 *  - /account/media (السفير: أزرار مشاركة + فلتر)
 *  - /admin/ambassador-media (شارات المنصّات على البطاقات)
 */
import { dedupeWhatsAppUrls } from "@/lib/whatsapp-caption";



export type PlatformId =
  | "instagram"
  | "tiktok"
  | "snapchat"
  | "youtube"
  | "whatsapp"
  | "facebook"
  | "pinterest"
  | "twitter"
  | "telegram";

export type MediaTypeId = "video" | "image" | "story" | "caption";

/** كلّ ملف ميديا يعرض كلّ أيقونات السوسيال (السفير يختار بنفسه) */
const _ALL: PlatformId[] = [
  "instagram",
  "tiktok",
  "snapchat",
  "youtube",
  "whatsapp",
  "facebook",
  "pinterest",
  "twitter",
  "telegram",
];
export const MEDIA_TYPE_TO_PLATFORMS: Record<MediaTypeId, PlatformId[]> = {
  video: _ALL,
  story: _ALL,
  image: _ALL,
  caption: _ALL,
};

/** منصّات تدعم مشاركة مباشرة من المتصفّح (web intent) */
export const WEB_SHAREABLE: PlatformId[] = [
  "whatsapp",
  "telegram",
  "twitter",
  "facebook",
  "pinterest",
];

/** منصّات تتطلّب فتح التطبيق يدوياً (نُحمّل + ننسخ + نُرشد) */
export const APP_ONLY: PlatformId[] = [
  "instagram",
  "tiktok",
  "snapchat",
  "youtube",
];

export type ShareInput = {
  text: string;       // النصّ + الرابط
  url?: string;       // رابط المنتج (لـ Pinterest يحتاج رابط منفصل)
  mediaUrl?: string;  // رابط الصورة لـ Pinterest
};

/** يُعيد رابط مشاركة جاهز للفتح في tab جديد، أو null إن لم تكن المنصّة تدعم web sharing */
export function buildShareUrl(
  platform: PlatformId,
  input: ShareInput,
): string | null {
  // درع طبقة ٣: لواتساب نُنظّف أيّ رابط مكرَّر داخل النصّ قبل تكوين wa.me.
  const cleanedText =
    platform === "whatsapp" ? dedupeWhatsAppUrls(input.text) : input.text;
  const text = encodeURIComponent(cleanedText);
  const url = encodeURIComponent(input.url ?? "");
  const media = encodeURIComponent(input.mediaUrl ?? "");

  switch (platform) {
    case "whatsapp":
      return `https://wa.me/?text=${text}`;
    case "telegram":
      return `https://t.me/share/url?url=${url}&text=${text}`;
    case "twitter":
      return `https://twitter.com/intent/tweet?text=${text}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
    case "pinterest":
      return `https://pinterest.com/pin/create/button/?url=${url}&media=${media}&description=${text}`;
    default:
      return null;
  }
}

/** قائمة كل المنصّات بترتيب عرض ثابت */
export const ALL_PLATFORMS: PlatformId[] = [
  "instagram",
  "tiktok",
  "snapchat",
  "youtube",
  "whatsapp",
  "facebook",
  "pinterest",
  "twitter",
  "telegram",
];

/**
 * صفحات رفع/نشر سريعة لكل منصّة (ديسكتوب).
 * نفتحها في تبويب جديد بعد تنزيل الملف ونسخ النصّ، ليستطيع السفير اللصق فوراً.
 */
/**
 * يبني رابط ملف الشخصيّة العامّ من handle (يُزيل @ و /).
 * يُستخدم في فورم السفراء وعرض الأدمن.
 */
export function buildProfileUrl(platform: string, handle: string): string | null {
  const h = handle.trim().replace(/^@+/, "").replace(/\/+$/, "");
  if (!h) return null;
  // إن كان المستخدم لصق رابطاً كاملاً، نعيده كما هو
  if (/^https?:\/\//i.test(h)) return h;
  switch (platform) {
    case "instagram":
      return `https://instagram.com/${h}`;
    case "tiktok":
      return `https://www.tiktok.com/@${h}`;
    case "youtube":
      // قد يكون @handle أو UCxxxx
      return h.startsWith("UC")
        ? `https://www.youtube.com/channel/${h}`
        : `https://www.youtube.com/@${h}`;
    case "snapchat":
      return `https://www.snapchat.com/add/${h}`;
    case "twitter":
      return `https://twitter.com/${h}`;
    case "facebook":
      return `https://www.facebook.com/${h}`;
    case "pinterest":
      return `https://www.pinterest.com/${h}`;
    case "telegram":
      return `https://t.me/${h}`;
    default:
      return null;
  }
}

export const PLATFORM_UPLOAD_URL: Record<PlatformId, string> = {
  instagram: "https://www.instagram.com/",
  tiktok: "https://www.tiktok.com/upload",
  snapchat: "https://web.snapchat.com/",
  youtube: "https://studio.youtube.com/",
  whatsapp: "https://web.whatsapp.com/",
  facebook: "https://www.facebook.com/",
  pinterest: "https://www.pinterest.com/pin-builder/",
  twitter: "https://twitter.com/compose/tweet",
  telegram: "https://web.telegram.org/",
};

/**
 * يطوّع ما يكتبه السفير في حقل «رابط الحساب» إلى رابط كامل:
 * - "@user" أو "user" → رابط المنصّة
 * - "instagram.com/user" → https://instagram.com/user
 * - رابط كامل → كما هو (بعد تنظيف المسافات)
 */
export function normalizeProfileUrl(platform: string, raw: string): string {
  const v = raw.trim().replace(/\s+/g, "");
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (/^[\w-]+(\.[\w-]+)+\//.test(v) || /^(www\.)[\w-]+(\.[\w-]+)+/.test(v)) {
    return `https://${v.replace(/^\/+/, "")}`;
  }
  return buildProfileUrl(platform, v) ?? `https://${v}`;
}
